import React, { useEffect, useState } from 'react';
import useStoryBible from '../../hooks/useStoryBible';
import { storyBibleApi } from '../../api/storyBible';
import {
  LOADING_BOOK_NOTES,
  SAVE_TO_BOOK_NOTES,
} from '../../utils/writerCopy';
import './AddToBibleModal.css';

/**
 * SaveToCharacterModal — "Save to Bible" (dossier-consolidation, work item 5).
 *
 * Distinct from AddToBibleModal (which segments a message into NEW canon
 * entries): this attaches the message VERBATIM as a note on an EXISTING
 * character. The picker lists only confirmed, named Story Bible entries —
 * entities-bind-never-author, same rule CharacterPicker/useStoryBible
 * already enforce for the Story Board. No inline character creation.
 *
 * Props:
 * - storyId, messageContent, messageId, sessionId
 * - onClose(): close without saving
 * - onSaved(entryName): called after a successful save
 */
const SaveToCharacterModal = ({ storyId, messageContent, messageId, sessionId, onClose, onSaved }) => {
  const storyBible = useStoryBible(storyId);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { storyBible.ensureLoaded(); }, [storyBible]);

  // Book Sheet Cards repoint: confirmedEntries is now filtered to
  // category === 'Character' (or legacy no-category rows) by useStoryBible
  // itself — this modal's own doc comment already claimed that scope, this
  // just makes it real. Group by groupLabel (category, or the legacy
  // section title) for the same reason CharacterPicker does.
  const bySection = storyBible.confirmedEntries.reduce((acc, entry) => {
    const label = entry.groupLabel || entry.sectionTitle;
    (acc[label] = acc[label] || []).push(entry);
    return acc;
  }, {});

  const handlePick = async (entry) => {
    setError('');
    setIsSaving(true);
    try {
      await storyBibleApi.addNote(storyId, entry.id, messageContent || '', messageId || null, sessionId || null);
      if (onSaved) onSaved(entry.name);
      onClose();
    } catch (err) {
      console.error('Failed to save note to Bible entry:', err);
      setError('Could not save this. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="add-to-bible-backdrop" onClick={onClose}>
      <div
        className="add-to-bible-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={SAVE_TO_BOOK_NOTES}
      >
        <div className="add-to-bible-header">
          <div>
            <h3>{SAVE_TO_BOOK_NOTES}</h3>
            <p className="add-to-bible-subtitle">
              Pick an existing character to attach this message to as a note. Characters are
              added in Book Notes — not from here.
            </p>
          </div>
          <button
            className="add-to-bible-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="add-to-bible-content">
          {storyBible.isLoading && <div className="add-to-bible-loading">{LOADING_BOOK_NOTES}</div>}
          {!storyBible.isLoading && storyBible.error && (
            <p className="add-to-bible-empty">{storyBible.error}</p>
          )}
          {!storyBible.isLoading && !storyBible.error && storyBible.confirmedEntries.length === 0 && (
            <p className="add-to-bible-empty">
              No characters in Book Notes yet. Add one there first, then come back to save this.
            </p>
          )}
          {!storyBible.isLoading && !storyBible.error && Object.entries(bySection).map(([sectionTitle, entries]) => (
            <div key={sectionTitle} className="save-to-character__section">
              <div className="save-to-character__section-title">{sectionTitle}</div>
              {entries.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="save-to-character__item"
                  disabled={isSaving}
                  onClick={() => handlePick(entry)}
                >
                  {entry.name}
                </button>
              ))}
            </div>
          ))}
          {error && <span className="add-to-bible-error">{error}</span>}
        </div>

        <div className="add-to-bible-footer">
          <button
            type="button"
            className="add-to-bible-cancel-btn"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveToCharacterModal;
